import {
  type MaybeComputedElementRef,
  unrefElement,
  useElementBounding,
  useResizeObserver,
  useWindowSize,
} from '@vueuse/core';
import { computed } from 'vue';

const MIN_HEIGHT = 400;

function previousSiblings(element: Element | null | undefined) {
  const siblings: HTMLElement[] = [];
  for (
    let sibling = element?.previousElementSibling;
    sibling;
    sibling = sibling.previousElementSibling
  ) {
    if (sibling instanceof HTMLElement) siblings.push(sibling);
  }
  return siblings;
}

/**
 * Height from `target` to the bottom of the viewport, for a table that renders
 * only the rows in view and so needs a bounded height. Measured from the page
 * top so that scrolling does not resize it, and re-measured when the content
 * above it in its parent grows or shrinks, such as wrapping filter chips.
 */
export function useViewportFillHeight(target: MaybeComputedElementRef) {
  const { top, update } = useElementBounding(target, { windowScroll: false });
  const { height: windowHeight } = useWindowSize();
  useResizeObserver(() => previousSiblings(unrefElement(target)), update);

  return computed(() => {
    const offsetFromPageTop = top.value + window.scrollY;
    return `${Math.max(MIN_HEIGHT, windowHeight.value - offsetFromPageTop)}px`;
  });
}
